<template>
  <el-drawer
    :title="$t('externalTransaction.detail.title')"
    :visible="true"
    :append-to-body="true"
    destroy-on-close
    size="650px"
    class="reservation-order-detail"
    @close="$emit('close')"
  >
    <div v-loading="isLoadingDrawer" class="px-[20px]">
      <!-- 基本資訊 -->
      <BobDisplayForm :config="displayFormConfig" :fields="baseInfoFields" />

      <!-- 消費品項 -->
      <hr style="margin: 32px 0">
      <p class="forminfo-title">{{ $t('externalTransaction.detail.items') }}</p>
      <div class="flex flex-col gap-[32px] mt-[10px]">
        <BobDisplayForm
          v-for="(itemFields, idx) in salesFields"
          :key="idx"
          :config="displayFormConfig"
          :fields="itemFields"
        />
      </div>

      <!-- 折扣 -->
      <template v-if="discountFields.length">
        <hr style="margin: 32px 0">
        <p class="forminfo-title">{{ $t('externalTransaction.detail.discount') }}</p>
        <div class="mt-[10px]">
          <BobDisplayForm :config="displayFormConfig" :fields="discountFields" />
        </div>
      </template>

      <!-- 支付方式 -->
      <template v-if="paymentFields.length">
        <hr style="margin: 32px 0">
        <p class="forminfo-title">{{ $t('externalTransaction.detail.payment') }}</p>
        <div class="mt-[10px]">
          <BobDisplayForm :config="displayFormConfig" :fields="paymentFields" />
        </div>
      </template>

      <!-- 本次可獲得 -->
      <template v-if="rewardFields.length">
        <hr style="margin: 32px 0">
        <p class="forminfo-title">{{ $t('externalTransaction.detail.reward') }}</p>
        <div class="mt-[10px]">
          <BobDisplayForm :config="displayFormConfig" :fields="rewardFields" />
        </div>
      </template>

      <!-- 事件行銷獎勵 Block -->
      <EventMarketingRewardBlock
        v-if="!displayData.isNegative"
        v-loading="isLoadingRewards"
        :records="rewardRecords"
        @goTransaction="$emit('goTransaction', $event)"
      />

      <!-- 負向交易提示 -->
      <template v-if="displayData.isNegative && negativeTransactionHint">
        <hr style="margin: 32px 0">
        <p class="forminfo-title">{{ $t('externalTransaction.detail.eventMarketing') }}</p>
        <GrayBlockContainer class="text-gray-60 mt-[10px] !p-2 whitespace-pre-line">
          {{ negativeTransactionHint.text }}
          <a
            v-if="negativeTransactionHint.originalTransactionId"
            class="text-primary-100 underline cursor-pointer"
            @click.prevent="$emit('goTransaction', { type: 'exactTransactionId', value: negativeTransactionHint.originalTransactionId })"
          >
            {{ negativeTransactionHint.originalTransactionId }}
          </a>
          <template v-else>-</template>
          <span v-if="negativeTransactionHint.originalTransactionId">{{ $t('externalTransaction.detail.viewFullTransaction') }}</span>
        </GrayBlockContainer>
      </template>

      <!-- Footer -->
      <hr style="margin: 32px 0">
      <BobDisplayForm :config="displayFormConfig" :fields="footerFields" />

      <MarkExternalTransactionModal
        v-if="showMarkModal"
        :record="recordData"
        @close="showMarkModal = false"
        @updateStatus="updateStatus"
      />
      <ExternalTransactionVoidModal
        v-if="showVoidModal"
        :record="{ ...record, ...recordData, eventPlaybookAwardRecords: rewardRecords }"
        @close="showVoidModal = false"
        @refresh="$emit('refresh'); $emit('close')"
      />
      <div class="flex justify-end" style="margin-top: 70px;">
        <BaseElButton plain style="width: 100px;" @click="$emit('close')">{{ $t('externalTransaction.detail.back') }}</BaseElButton>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onBeforeMount, ref, watch } from 'vue'
import Tag from '@/components/Tag/Tag.vue'
import BobDisplayForm from '@/components/BobDisplayForm.vue'
import { FindMemberExternalTransaction, VoidMemberExternalTransaction, GetMemberExternalTransactionEventPlaybookAwardRecords } from '@/api/memberExternalTransaction'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import EventMarketingRewardBlock from './EventMarketingRewardBlock.vue'
import { get, map, forEach } from 'lodash'
import { formatDate, formatDateRange } from '@/utils/date'
import { rewardActivityTypeConfig } from '@/config/rewardActivity'
import { localeString } from '@/utils/helper'
import { salesItemFieldConfig, originsConfig, memberExternalTransactionStatusConfig, markedStatusConfig, memberExternalTransactionTypeConfig } from '@/config/externalTransaction'
import MarkExternalTransactionModal from './MarkExternalTransactionModal.vue'
import ExternalTransactionVoidModal from './ExternalTransactionVoidModal.vue'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import { usePermissions } from '@/use/permissions'

// ── 常數 ──────────────────────────────────────────────
const TRANSACTION_SOURCE = {
  dudoo: 'dudoo',
  weiby: 'weiby',
  tkhspc: 'tkhspc',
  pospal: 'pospal',
  drfoot: 'drfoot',
  opentix: originsConfig.opentix.value,
  cyjPos: originsConfig.cyjPos.value,
}
const NEGATIVE_TRANSACTION_TYPES = ['userOrderVoid', 'userOrderRefund']
const VOID_LABEL_KEYS = ['manualInvalid', 'systemInvalid']
const CREATED_STATUS = 'created'

// BobDisplayForm 共用設定
const displayFormConfig = { labelWidth: '132px', rowGap: 10, lineHeight: '24px' }

// ── 純函式 ────────────────────────────────────────────
/**
 * 將相同名稱的折扣/支付項目合計金額
 */
const combineItems = (items) => {
  if (!items) return []
  const itemMap = new Map()
  forEach(items, (item) => {
    if (itemMap.has(item.name)) {
      itemMap.get(item.name).amount = Math.round(itemMap.get(item.name).amount + item.amount)
    } else {
      itemMap.set(item.name, { ...item, amount: Math.round(item.amount) })
    }
  })
  return Array.from(itemMap.values())
}

export default defineComponent({
  name: 'ExternalTransactionRecordDetail',
  components: {
    BobDisplayForm,
    MarkExternalTransactionModal,
    ExternalTransactionVoidModal,
    EventMarketingRewardBlock,
    GrayBlockContainer,
  },
  props: {
    record: { type: Object, default: () => ({}) },
    findRecordApi: { type: Function, default: FindMemberExternalTransaction },
  },
  emits: ['close', 'refresh', 'goTransaction'],
  setup (props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { simpleFetch } = useFetch()
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const recordData = ref({})
    const showMarkModal = ref(false)
    const showVoidModal = ref(false)
    const rewardRecords = ref([])
    const isLoadingRewards = ref(false)
    const isLoadingDrawer = ref(false)

    // ── 正規化資料層 ──────────────────────────────────
    const displayData = computed(() => {
      const data = { ...props.record, ...recordData.value }
      const origin = get(data, 'source') || get(data, 'origin', '-')
      const originLower = typeof origin === 'string' ? origin.toLowerCase() : ''
      const status = get(data, 'status')
      const statusCfg = memberExternalTransactionStatusConfig[status] || {}
      const labels = get(data, 'labels', []) || []
      const voidLabel = labels.find(l => VOID_LABEL_KEYS.includes(l.key)) || null
      const type = get(data, 'type')

      return {
        transactionId: get(data, 'transactionId', '-'),
        origin,
        originLower,
        type,
        typeLabel: get(memberExternalTransactionTypeConfig, `${type}.label`, '一般訂單'),
        customerName: get(data, 'customerName') || get(data, 'memberName') || get(data, 'Member.name', '-'),
        phone: get(data, 'customerPhone') || get(data, 'phone', '-'),
        amount: localeString(get(data, 'amount', '-')),
        sellAmount: localeString(get(data, 'sellAmount', '-')),
        transactionTime: get(data, 'transactionTime') || get(data, 'time'),
        status,
        statusLabel: statusCfg.label || '-',
        statusTagType: statusCfg.type || 'info',
        isInvalid: get(data, 'isInvalid'),
        labels,
        voidLabel,
        hasVoidLabel: Boolean(voidLabel),
        branchName: get(recordData.value, 'Branch.name') || get(data, 'branchName') || get(data, 'BranchNo', '-'),
        transactionNote: get(data, 'transactionNote', '-'),
        items: get(recordData.value, 'items', []),
        source: get(recordData.value, 'source', ''),
        discountDetails: get(recordData.value, 'discountDetails'),
        paymentDetails: get(recordData.value, 'paymentDetails') || get(props.record, 'paymentDetails'),
        rewardActivityRecords: get(data, 'RewardActivityRecords', []),
        createdAt: get(recordData.value, 'createdAt'),
        isNegative: NEGATIVE_TRANSACTION_TYPES.includes(type),
        originalTransactionId: get(data, 'originalTransactionId') || get(data, 'originTransactionId') || null,
      }
    })

    // ── 各區 Fields ───────────────────────────────────

    const baseInfoFields = computed(() => {
      const fields = [
        { prop: 'transactionId', label: vm.$t('externalTransaction.detail.transactionId'), body: { render: () => get(displayData.value, 'transactionId') } },
        { prop: 'origin', label: vm.$t('externalTransaction.detail.source'), body: { render: () => get(displayData.value, 'origin') } },
        { prop: 'type', label: vm.$t('externalTransaction.detail.orderType'), body: { render: () => get(displayData.value, 'typeLabel') } },
        {
          prop: 'customerProfile',
          label: '會員姓名 / 手機',
          body: {
            render: () => {
              const name = get(displayData.value, 'customerName', '-')
              const phone = get(displayData.value, 'phone', '-')
              return name && name !== '-' ? `${name} / ${phone}` : phone
            },
          },
        },
        { prop: 'phone', label: vm.$t('externalTransaction.detail.phone'), body: { render: () => get(displayData.value, 'phone') } },
        { prop: 'amount', label: vm.$t('externalTransaction.detail.amount'), body: { render: () => get(displayData.value, 'amount') } },
        { prop: 'sellAmount', label: vm.$t('externalTransaction.detail.sellAmount'), body: { render: () => get(displayData.value, 'sellAmount') } },
        {
          prop: 'transactionTime',
          label: vm.$t('externalTransaction.detail.time'),
          body: { render: () => get(displayData.value, 'transactionTime') ? formatDate(get(displayData.value, 'transactionTime')) : '-' },
        },
        {
          prop: 'isInvalid',
          label: vm.$t('externalTransaction.detail.isInvalid'),
          body: {
            component: Tag,
            props: { type: get(displayData.value, 'isInvalid') ? 'error' : 'info' },
            render: () => get(displayData.value, 'isInvalid') ? vm.$t('externalTransaction.detail.yes') : vm.$t('externalTransaction.detail.no'),
          },
        },
        {
          prop: 'status',
          label: vm.$t('externalTransaction.detail.status'),
          body: {
            component: Tag,
            props: { type: get(displayData.value, 'statusTagType') },
            render: () => get(displayData.value, 'statusLabel'),
          },
        },
      ]

      // 交易門市：dudoo / weiby / cyjPos 顯示
      if ([TRANSACTION_SOURCE.dudoo, TRANSACTION_SOURCE.weiby].includes(get(displayData.value, 'originLower')) || get(displayData.value, 'origin') === TRANSACTION_SOURCE.cyjPos) {
        fields.splice(4, 0, {
          prop: 'branchName',
          label: vm.$t('externalTransaction.detail.branch'),
          body: { render: () => get(displayData.value, 'branchName') },
        })
      }

      // 作廢/退貨按鈕：status=created 且未作廢，且具備人工作廢/退款權限
      if (
        get(displayData.value, 'status') === CREATED_STATUS &&
        !get(displayData.value, 'isInvalid') &&
        checkAction('admin.memberExternalTransaction.manualVoidOrRefund')
      ) {
        fields.push({
          prop: 'voidAction',
          label: vm.$t('externalTransaction.detail.voidAction'),
          body: {
            component: 'BaseElButton',
            props: { type: 'text' },
            on: { click: () => { showVoidModal.value = true } },
            render: () => vm.$t('externalTransaction.detail.voidRefund'),
          },
        })
      }

      // 訂單標記：status=created 且未標記，且具備標記作廢權限
      if (
        get(displayData.value, 'status') === CREATED_STATUS &&
        !get(displayData.value, 'hasVoidLabel') &&
        checkAction('admin.memberExternalTransaction.addInvalidLabel')
      ) {
        fields.push({
          prop: 'markAction',
          label: vm.$t('externalTransaction.detail.markAction'),
          body: {
            component: 'BaseElButton',
            props: { type: 'text' },
            on: { click: () => { showMarkModal.value = true } },
            render: () => vm.$t('externalTransaction.detail.manualMark'),
          },
        })
      } else if (get(displayData.value, 'hasVoidLabel')) {
        fields.push({
          prop: 'markAction',
          label: vm.$t('externalTransaction.detail.markAction'),
          body: { render: () => get(markedStatusConfig[get(displayData.value, 'voidLabel.key')], 'optionLabel', '-') },
        })
      }

      // tkhspc || cyj 訂單備註
      if (get(displayData.value, 'originLower') === TRANSACTION_SOURCE.tkhspc || get(displayData.value, 'origin') === TRANSACTION_SOURCE.cyjPos) {
        fields.push({
          prop: 'transactionNote',
          label: vm.$t('externalTransaction.detail.note'),
          body: { render: () => get(displayData.value, 'transactionNote') },
        })
      }

      return fields
    })

    const salesFields = computed(() => {
      return map(get(displayData.value, 'items'), (item) => {
        const nameLabelKey = `${get(displayData.value, 'source')}.name.label`
        const fields = [
          {
            prop: 'name',
            label: get(salesItemFieldConfig, nameLabelKey, vm.$t('externalTransaction.salesItem.name')),
            body: { render: () => get(item, 'name', '-') },
          },
          { prop: 'itemNo', label: 'ID', body: { render: () => get(item, 'itemNo', '-') } },
          { prop: 'price', label: vm.$t('externalTransaction.salesItem.price'), body: { render: () => localeString(get(item, 'price', '-')) } },
          { prop: 'quantity', label: vm.$t('externalTransaction.salesItem.quantity'), body: { render: () => get(item, 'quantity', '-') } },
        ]

        // drfoot：在 ID 前插入編號
        if (get(displayData.value, 'source').toLowerCase() === TRANSACTION_SOURCE.drfoot) {
          fields.splice(1, 0, {
            prop: 'drfootNo',
            label: vm.$t('externalTransaction.salesItem.drfoot.itemNo'),
            body: { render: () => get(item, 'itemNo', '-') },
          })
        }

        // opentix：附加場次起迄時間
        if (get(displayData.value, 'source') === TRANSACTION_SOURCE.opentix) {
          fields.push({
            prop: 'eventTimeRange',
            label: get(salesItemFieldConfig, `${get(displayData.value, 'source')}.eventTimeRange.label`, vm.$t('externalTransaction.salesItem.opentix.eventTimeRange')),
            body: {
              render: () => formatDateRange({
                dates: [
                  get(item, 'context.opentixEventStartTime') * 1000,
                  get(item, 'context.opentixEventEndTime') * 1000,
                ],
              }),
            },
          })
        }

        // pospal：附加訂單備註
        if (get(displayData.value, 'source').toLowerCase() === TRANSACTION_SOURCE.pospal) {
          fields.push({
            prop: 'note',
            label: vm.$t('externalTransaction.salesItem.note'),
            body: { render: () => get(item, 'note', '-') },
          })
        }

        return fields
      })
    })

    const discountFields = computed(() => {
      const combined = combineItems(displayData.value.discountDetails)
      return combined.map(item => ({
        prop: item.name,
        label: item.name,
        body: { render: () => localeString(item.amount) },
      }))
    })

    const paymentFields = computed(() => {
      const data = displayData.value.paymentDetails
      if (!data) return []
      return data.map(item => ({
        prop: item.type,
        label: item.type,
        body: { render: () => localeString(Math.round(item.amount)) },
      }))
    })

    const rewardFields = computed(() => {
      return map(displayData.value.rewardActivityRecords, reward => {
        let amount = get(reward, 'rewardAmount')
        if (!Number.isNaN(amount)) amount = amount.toLocaleString()
        if (reward.rewardType === 'CASHBACK') {
          return {
            prop: 'cashback',
            label: rewardActivityTypeConfig.CASHBACK.label,
            body: { render: () => `$ ${amount}` },
          }
        }
        if (reward.rewardType === 'POINT') {
          return {
            prop: 'point',
            label: rewardActivityTypeConfig.POINT.label,
            body: { render: () => `${amount} 點` },
          }
        }
        return null
      }).filter(Boolean)
    })

    const footerFields = computed(() => [
      {
        prop: 'createdAt',
        label: vm.$t('externalTransaction.detail.createdAt'),
        body: { render: () => get(displayData.value, 'createdAt') ? formatDate(get(displayData.value, 'createdAt')) : '-' },
      },
    ])

    // ── 負向交易提示 ──────────────────────────────────
    const negativeTransactionHint = computed(() => {
      if (get(displayData.value, 'type') === 'userOrderVoid') {
        return { text: vm.$t('externalTransaction.detail.voidHint'), originalTransactionId: get(displayData.value, 'originalTransactionId') }
      }
      if (get(displayData.value, 'type') === 'userOrderRefund') {
        return { text: vm.$t('externalTransaction.detail.refundHint'), originalTransactionId: get(displayData.value, 'originalTransactionId') }
      }
      return null
    })

    // ── API 操作 ──────────────────────────────────────
    const updateStatus = async () => {
      const [, err] = await VoidMemberExternalTransaction({
        shopId: shopId.value,
        id: get(props.record, 'id'),
      })
      if (err) return window.$message.error(err)
      window.$message.success(vm.$t('externalTransaction.detail.updateSuccess'))
      emit('close')
      emit('refresh')
      showMarkModal.value = false
    }

    const findMemberExternalTransaction = async () => {
      // 外層若顯式傳入 null（Vue prop default 只在 undefined 時生效），這裡要 fallback 回店家層級 API
      await simpleFetch(props.findRecordApi || FindMemberExternalTransaction, {
        shopId: shopId.value,
        id: get(props.record, 'id'),
      }, (res) => {
        recordData.value = res
      })
    }

    const getRewardRecords = async () => {
      isLoadingRewards.value = true
      const [res, err] = await GetMemberExternalTransactionEventPlaybookAwardRecords({
        shopId: shopId.value,
        id: get(props.record, 'id'),
      })
      isLoadingRewards.value = false
      if (err) return
      rewardRecords.value = get(res, 'eventPlaybookAwardRecords', res || [])
    }

    const fetchDetailData = async () => {
      const id = get(props.record, 'id')
      if (!id) return
      isLoadingDrawer.value = true
      try {
        await findMemberExternalTransaction()
        await getRewardRecords()
      } catch (error) {
        // 不讓例外中斷整個抽屜，否則畫面會靜默空白、看不出是查詢失敗
        console.error('[ExternalTransactionRecordDetail] fetchDetailData failed', error)
        window.$message.error(vm.$t('externalTransaction.detail.fetchFailed'))
      } finally {
        isLoadingDrawer.value = false
      }
    }

    onBeforeMount(async () => {
      await fetchDetailData()
    })

    watch(() => get(props.record, 'id'), async (newId) => {
      if (!newId) return
      await fetchDetailData()
    })

    return {
      displayData,
      displayFormConfig,
      baseInfoFields,
      salesFields,
      discountFields,
      paymentFields,
      rewardFields,
      footerFields,
      negativeTransactionHint,
      recordData,
      showMarkModal,
      showVoidModal,
      updateStatus,
      rewardRecords,
      isLoadingRewards,
      isLoadingDrawer,
    }
  },
})
</script>

<style lang="postcss" scoped>
.forminfo-title {
  @apply text-primary-100 font-medium text-normal;
}
</style>
